import { useState, useEffect, useCallback } from 'react';
import { Order, OrderItem } from '../types/orders';
import { fetchOrderItems, assignSerialToOrderItem, unassignSerial } from '../services/ordersApi';

interface ValidationError {
  message: string;
  existingOrderId?: number;
  existingOrderNumber?: string;
}

interface BatchAssignment {
  orderItemId: number;
  deviceId?: number;
  serialNumber?: string;
  slotId: string;
}

interface BatchResult {
  success: boolean;
  error?: ValidationError;
}

interface UseOrderItemsReturn {
  order: Order | null;
  orderItems: OrderItem[];
  loading: boolean;
  error: string;
  validating: Record<string, boolean>;
  validationErrors: Record<string, ValidationError | string>;
  fetchOrderItems: () => Promise<void>;
  assignSerial: (
    orderItemId: number,
    deviceId?: number,
    serialNumber?: string,
    slotId?: string
  ) => Promise<boolean>;
  batchAssignSerials: (
    assignments: BatchAssignment[]
  ) => Promise<Record<string, BatchResult>>;
  unassignSerialNumber: (assignmentId: number, serialNumber: string, reason?: string, hardDelete?: boolean) => Promise<boolean>;
  clearValidationError: (slotId: string) => void;
  setError: (error: string) => void;
}

export const useOrderItems = (orderId: string | undefined): UseOrderItemsReturn => {
  const [order, setOrder] = useState<Order | null>(null);
  const [orderItems, setOrderItems] = useState<OrderItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [validating, setValidating] = useState<Record<string, boolean>>({});
  const [validationErrors, setValidationErrors] = useState<Record<string, ValidationError | string>>({});

  const loadOrderItems = useCallback(async () => {
    if (!orderId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const data = await fetchOrderItems(orderId);
      
      if (data.success) {
        setOrder(data.order);
        setOrderItems(data.items);
      }
    } catch (err: any) {
      console.error('Error fetching order items:', err);
      setError(err.response?.data?.message || 'Failed to fetch order items');
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  const assignSerial = useCallback(async (
    orderItemId: number,
    deviceId?: number,
    serialNumber?: string,
    slotId?: string
  ): Promise<boolean> => {
    if (!orderId) {
      setError('Order ID is missing. Please refresh the page.');
      return false;
    }

    // Use provided slotId or fallback to orderItemId for backward compatibility
    const finalSlotId = slotId || `${orderItemId}`;
    try {
      setValidating(prev => ({ ...prev, [finalSlotId]: true }));
      setValidationErrors(prev => ({ ...prev, [finalSlotId]: '' }));
      
      const result = await assignSerialToOrderItem(orderId, orderItemId, deviceId, serialNumber);

      if (result.success) {
        await loadOrderItems();
        return true;
      } else {
        // Handle case where API returns success: false without throwing (shouldn't happen but be safe)
        const errorData: ValidationError = {
          message: result.message || 'Failed to assign serial number',
          existingOrderId: (result as any).existingOrderId,
          existingOrderNumber: (result as any).existingOrderNumber
        };
        setValidationErrors(prev => ({
          ...prev,
          [finalSlotId]: errorData
        }));
        return false;
      }
    } catch (err: any) {
      // Extract error message and order info from response
      const errorData: ValidationError = {
        message: 
          err.response?.data?.message || 
          err.response?.data?.error || 
          err.message || 
          'Failed to assign serial number',
        existingOrderId: err.response?.data?.existingOrderId,
        existingOrderNumber: err.response?.data?.existingOrderNumber
      };
      
      // Only log unexpected errors (5xx) or network errors to console
      // 400 errors are expected validation errors and are displayed to the user
      if (!err.response || err.response.status >= 500) {
        console.error('Error assigning serial:', err);
      }
      
      setValidationErrors(prev => ({
        ...prev,
        [finalSlotId]: errorData
      }));
      return false;
    } finally {
      setValidating(prev => ({ ...prev, [finalSlotId]: false }));
    }
  }, [orderId, loadOrderItems]);

  const batchAssignSerials = useCallback(async (
    assignments: BatchAssignment[]
  ): Promise<Record<string, BatchResult>> => {
    if (!orderId) {
      setError('Order ID is missing. Please refresh the page.');
      return {};
    }

    const results: Record<string, BatchResult> = {};
    const slotIds = assignments.map(a => a.slotId);

    // Mark all slots as validating
    setValidating(prev => {
      const next = { ...prev };
      slotIds.forEach(id => { next[id] = true; });
      return next;
    });
    setValidationErrors(prev => {
      const next = { ...prev };
      slotIds.forEach(id => { delete next[id]; });
      return next;
    });

    for (const assignment of assignments) {
      try {
        const result = await assignSerialToOrderItem(
          orderId,
          assignment.orderItemId,
          assignment.deviceId,
          assignment.serialNumber
        );

        if (result.success) {
          results[assignment.slotId] = { success: true };
        } else {
          const errorData: ValidationError = {
            message: result.message || 'Failed to assign serial number',
            existingOrderId: (result as any).existingOrderId,
            existingOrderNumber: (result as any).existingOrderNumber
          };
          results[assignment.slotId] = { success: false, error: errorData };
        }
      } catch (err: any) {
        const errorData: ValidationError = {
          message:
            err.response?.data?.message ||
            err.response?.data?.error ||
            err.message ||
            'Failed to assign serial number',
          existingOrderId: err.response?.data?.existingOrderId,
          existingOrderNumber: err.response?.data?.existingOrderNumber
        };
        if (!err.response || err.response.status >= 500) {
          console.error('Error assigning serial:', err);
        }
        results[assignment.slotId] = { success: false, error: errorData };
      }
    }

    // Set validation errors for failed slots
    const errorEntries = Object.entries(results).filter(([, r]) => !r.success && r.error);
    if (errorEntries.length > 0) {
      setValidationErrors(prev => {
        const next = { ...prev };
        errorEntries.forEach(([slotId, r]) => { next[slotId] = r.error!; });
        return next;
      });
    }

    // Single refetch after all assignments
    await loadOrderItems();

    // Clear validating for all slots
    setValidating(prev => {
      const next = { ...prev };
      slotIds.forEach(id => { next[id] = false; });
      return next;
    });

    return results;
  }, [orderId, loadOrderItems]);

  const unassignSerialNumber = useCallback(async (
    assignmentId: number,
    serialNumber: string,
    reason?: string,
    hardDelete?: boolean
  ): Promise<boolean> => {
    try {
      const result = await unassignSerial(assignmentId, reason, hardDelete);
      
      if (result.success) {
        await loadOrderItems();
        return true;
      }
      return false;
    } catch (err: any) {
      console.error('Error unassigning serial:', err);
      setError(err.response?.data?.message || 'Failed to unassign serial number');
      return false;
    }
  }, [loadOrderItems]);

  useEffect(() => {
    loadOrderItems();
  }, [loadOrderItems]);

  return {
    order,
    orderItems,
    loading,
    error,
    validating,
    validationErrors,
    fetchOrderItems: loadOrderItems,
    assignSerial,
    batchAssignSerials,
    unassignSerialNumber,
    clearValidationError: (slotId: string) => {
      setValidationErrors(prev => {
        const next = { ...prev };
        delete next[slotId];
        return next;
      });
    },
    setError
  };
};

